"use client";

import { useCallback } from "react";
import { useTranslations } from "@/hooks/use-translations";

import {
  friendlyError as baseFriendlyError,
  type FriendlyErrorKey,
} from "@/lib/friendly-error";

/**
 * Language-aware friendlyError for client components.
 *
 * Same idea as friendlyError(err, fallback): turn any thrown thing into a sentence a member can act
 * on. The difference is that the recognised causes (offline, session expired, no permission, and so
 * on) come back in the member's own language, from the "errors" namespace, instead of always English.
 *
 * Note: this uses the app's own useTranslations (@/hooks/use-translations), NOT next-intl's client
 * hook. The app has its own i18n context and does not mount next-intl's client provider, so the raw
 * next-intl hook would throw during render (e.g. in the root layout, on the landing page).
 *
 * Usage:
 *   const friendlyError = useFriendlyError();
 *   ...
 *   toast.error(friendlyError(err, t("failedToJoinCommunity")));
 */
export function useFriendlyError() {
  const { t } = useTranslations("errors");
  return useCallback(
    (err: unknown, fallback?: string) =>
      baseFriendlyError(err, fallback, (key: FriendlyErrorKey) => t(key)),
    [t],
  );
}
